import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    default: { type: String, default: "table" },
    storageKey: { type: String, default: "view-switcher-view" }
  }

  static targets = ["view", "button"]

  connect() {
    this.show(this.activeView)
  }

  // Re-apply the active view when targets are (re)inserted, e.g. after a turbo
  // frame reload swaps the views/buttons inside this controller's scope.
  viewTargetConnected(element) {
    element.hidden = element.dataset.view !== this.activeView
  }

  buttonTargetConnected(element) {
    this.markButton(element, this.activeView)
  }

  switch({ params: { view } }) {
    if (!view) return
    this.persist(view)
    this.show(view)
  }

  // Single-button variant: flip to the other registered view. With exactly two
  // views ("card"/"table") this cycles between them; assumes a 2-view setup.
  toggle() {
    const views = [...new Set(this.viewTargets.map(el => el.dataset.view))]
    const next = views.find(view => view !== this.activeView) || this.activeView
    this.persist(next)
    this.show(next)
  }

  show(viewName) {
    this.viewTargets.forEach(el => {
      el.hidden = el.dataset.view !== viewName
    })

    this.buttonTargets.forEach(btn => this.markButton(btn, viewName))
  }

  // Screen readers hear which view is on through aria-pressed; the class is
  // the visual cue.
  markButton(button, viewName) {
    const selected = button.dataset.viewSwitcherViewParam === viewName
    button.classList.toggle("active", selected)
    button.setAttribute("aria-pressed", selected)
  }

  // The chosen view is stored in a cookie (not localStorage) so the server can
  // read it and render the correct view on first paint — no flash of both views
  // before this controller connects.
  get activeView() {
    return this.readCookie(this.storageKeyValue) || this.defaultValue
  }

  persist(view) {
    document.cookie = `${this.storageKeyValue}=${view}; path=/; max-age=31536000; SameSite=Lax`
  }

  readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  }
}
